import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Play, 
  ExternalLink,
  Star,
  Eye,
  EyeOff,
  X,
  Save,
  GripVertical,
  Award
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_OPTIONS = [
  { value: 'herb-savory', label: 'Herb & Savory', color: '#2D5016' },
  { value: 'citrus-garlic', label: 'Citrus Garlic', color: '#D4540A' },
  { value: 'smoky-savory', label: 'Smoky Savory', color: '#5C3D2E' },
  { value: 'seafood-seasoning', label: 'Seafood Seasoning', color: '#1E4D6B' },
  { value: 'full-set', label: 'Bundle / Full Set', color: '#2A2A2A' },
  { value: 'general', label: 'General / All Products', color: '#666666' }
];

const BADGE_OPTIONS = [
  { value: 'none', label: 'No Badge' },
  { value: 'most_popular', label: 'Most Popular', color: '#D97706' },
  { value: 'staff_pick', label: 'Staff Pick', color: '#047857' },
];

const AdminVideosPage = () => {
  const { token } = useAdminAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    platform: 'youtube',
    video_url: '',
    thumbnail_url: '',
    description: '',
    related_product: 'general',
    badge: 'none',
    featured: false,
    sort_order: 0,
    active: true
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/admin/videos`, {
        headers: getAuthHeaders()
      });
      setVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (video = null) => {
    if (video) {
      setEditingVideo(video);
      setFormData({
        title: video.title || '',
        platform: video.platform || 'youtube',
        video_url: video.video_url || '',
        thumbnail_url: video.thumbnail_url || '',
        description: video.description || '',
        related_product: video.related_product || 'general',
        badge: video.badge || 'none',
        featured: video.featured || false,
        sort_order: video.sort_order || 0,
        active: video.active !== false
      });
    } else {
      setEditingVideo(null);
      setFormData({
        title: '',
        platform: 'youtube',
        video_url: '',
        thumbnail_url: '',
        description: '',
        related_product: 'general',
        badge: 'none',
        featured: false,
        sort_order: videos.length,
        active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVideo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingVideo) {
        await axios.put(
          `${API}/admin/videos/${editingVideo.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Video updated successfully');
      } else {
        await axios.post(
          `${API}/admin/videos`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('Video created successfully');
      }
      fetchVideos();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save video');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await axios.delete(`${API}/admin/videos/${videoId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Video deleted');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const toggleActive = async (video) => {
    try {
      await axios.put(
        `${API}/admin/videos/${video.id}`,
        { active: !video.active },
        { headers: getAuthHeaders() }
      );
      toast.success(video.active ? 'Video hidden' : 'Video activated');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to update video');
    }
  };

  const toggleFeatured = async (video) => {
    try {
      await axios.put(
        `${API}/admin/videos/${video.id}`,
        { featured: !video.featured },
        { headers: getAuthHeaders() }
      );
      toast.success(video.featured ? 'Removed from featured' : 'Added to featured');
      fetchVideos();
    } catch (error) {
      toast.error('Failed to update video');
    }
  };

  const getProductInfo = (productId) => {
    return PRODUCT_OPTIONS.find(p => p.value === productId) || PRODUCT_OPTIONS[5];
  };

  // Auto-generate thumbnail URL from YouTube URL
  const handleVideoUrlChange = (url) => {
    setFormData(prev => ({ ...prev, video_url: url }));
    
    // Extract YouTube ID and generate thumbnail
    let videoId = '';
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    }
    
    if (videoId && !formData.thumbnail_url) {
      setFormData(prev => ({
        ...prev,
        thumbnail_url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#D4540A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-videos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Video Management</h1>
          <p className="text-[#888] mt-1">Manage cooking videos and YouTube content</p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-[#D4540A] hover:bg-[#B84709] text-white"
          data-testid="add-video-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Video
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-[#888] text-sm">Total Videos</p>
          <p className="text-2xl font-bold text-white">{videos.length}</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-[#888] text-sm">Featured</p>
          <p className="text-2xl font-bold text-yellow-400">{videos.filter(v => v.featured).length}</p>
        </div>
        <div className="bg-[#1a1a1a] rounded-xl p-4 border border-[#2a2a2a]">
          <p className="text-[#888] text-sm">Active</p>
          <p className="text-2xl font-bold text-green-400">{videos.filter(v => v.active).length}</p>
        </div>
      </div>

      {/* Videos List */}
      <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] overflow-hidden">
        {videos.length === 0 ? (
          <div className="p-12 text-center">
            <Play className="w-12 h-12 text-[#444] mx-auto mb-4" />
            <p className="text-[#888]">No videos yet</p>
            <p className="text-[#666] text-sm mt-1">Add your first cooking video to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-[#2a2a2a]">
            {videos.map((video) => {
              const productInfo = getProductInfo(video.related_product);
              return (
                <div 
                  key={video.id} 
                  className={`p-4 flex items-center gap-4 ${!video.active ? 'opacity-50' : ''}`}
                  data-testid={`video-row-${video.id}`}
                >
                  {/* Drag Handle */}
                  <GripVertical className="w-5 h-5 text-[#444] cursor-grab flex-shrink-0" />
                  
                  {/* Thumbnail */}
                  <div className="relative w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-[#2a2a2a]">
                    <img
                      src={video.thumbnail_url}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/320x180?text=Video'; }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play className="w-6 h-6 text-white" fill="white" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-white truncate">{video.title}</h3>
                      {video.featured && (
                        <Star className="w-4 h-4 text-yellow-400 flex-shrink-0" fill="#facc15" />
                      )}
                      {video.badge && video.badge !== 'none' && (
                        <span 
                          className="text-[10px] px-1.5 py-0.5 rounded font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: video.badge === 'most_popular' ? '#D97706' : '#047857' }}
                        >
                          {video.badge === 'most_popular' ? '★ Popular' : '✦ Staff Pick'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#888] line-clamp-1">{video.description || 'No description'}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span 
                        className="text-xs px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: productInfo.color }}
                      >
                        {productInfo.label}
                      </span>
                      <span className="text-xs text-[#666]">Order: {video.sort_order}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => window.open(video.video_url, '_blank')}
                      className="text-[#888] hover:text-white"
                      title="Watch on YouTube"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleFeatured(video)}
                      className={video.featured ? 'text-yellow-400' : 'text-[#888] hover:text-yellow-400'}
                      title={video.featured ? 'Remove from featured' : 'Add to featured'}
                    >
                      <Star className="w-4 h-4" fill={video.featured ? 'currentColor' : 'none'} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleActive(video)}
                      className={video.active ? 'text-green-400' : 'text-[#888] hover:text-green-400'}
                      title={video.active ? 'Hide video' : 'Show video'}
                    >
                      {video.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenModal(video)}
                      className="text-[#888] hover:text-white"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(video.id)}
                      className="text-[#888] hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-[#2a2a2a] flex items-center justify-between">
              <h3 className="font-semibold text-white">
                {editingVideo ? 'Edit Video' : 'Add New Video'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseModal}
                className="text-[#888] hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Title */}
              <div>
                <Label htmlFor="title" className="text-white">Video Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Grilled Salmon with Seafood Seasoning"
                  className="bg-[#111] border-[#333] text-white mt-1"
                  required
                />
              </div>

              {/* YouTube URL */}
              <div>
                <Label htmlFor="video_url" className="text-white">YouTube URL *</Label>
                <Input
                  id="video_url"
                  value={formData.video_url}
                  onChange={(e) => handleVideoUrlChange(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-[#111] border-[#333] text-white mt-1"
                  required
                />
              </div>

              {/* Thumbnail URL */}
              <div>
                <Label htmlFor="thumbnail_url" className="text-white">Thumbnail URL *</Label>
                <Input
                  id="thumbnail_url"
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                  placeholder="Auto-generated from YouTube URL or enter custom"
                  className="bg-[#111] border-[#333] text-white mt-1"
                  required
                />
                {formData.thumbnail_url && (
                  <div className="mt-2 w-40 h-24 rounded-lg overflow-hidden bg-[#2a2a2a]">
                    <img
                      src={formData.thumbnail_url}
                      alt="Thumbnail preview"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description" className="text-white">Short Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the video..."
                  className="w-full bg-[#111] border border-[#333] text-white mt-1 rounded-md px-3 py-2 text-sm min-h-[80px]"
                  maxLength={500}
                />
              </div>

              {/* Related Product */}
              <div>
                <Label htmlFor="related_product" className="text-white">Related Seasoning *</Label>
                <select
                  id="related_product"
                  value={formData.related_product}
                  onChange={(e) => setFormData(prev => ({ ...prev, related_product: e.target.value }))}
                  className="w-full bg-[#111] border border-[#333] text-white mt-1 rounded-md px-3 py-2 text-sm"
                  required
                >
                  {PRODUCT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Badge */}
              <div>
                <Label htmlFor="badge" className="text-white">Badge</Label>
                <select
                  id="badge"
                  value={formData.badge}
                  onChange={(e) => setFormData(prev => ({ ...prev, badge: e.target.value }))}
                  className="w-full bg-[#111] border border-[#333] text-white mt-1 rounded-md px-3 py-2 text-sm"
                  data-testid="badge-select"
                >
                  {BADGE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-[#666] mt-1">Highlight key videos for customers</p>
              </div>

              {/* Sort Order */}
              <div>
                <Label htmlFor="sort_order" className="text-white">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  className="bg-[#111] border-[#333] text-white mt-1 w-24"
                />
                <p className="text-xs text-[#666] mt-1">Lower numbers appear first</p>
              </div>

              {/* Toggles */}
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                    className="w-4 h-4 rounded border-[#333] bg-[#111] text-[#D4540A] focus:ring-[#D4540A]"
                  />
                  <span className="text-white text-sm">Featured on Homepage</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    className="w-4 h-4 rounded border-[#333] bg-[#111] text-[#D4540A] focus:ring-[#D4540A]"
                  />
                  <span className="text-white text-sm">Active (Visible to customers)</span>
                </label>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-[#2a2a2a]">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="border-[#333] text-white hover:bg-[#2a2a2a]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#D4540A] hover:bg-[#B84709] text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingVideo ? 'Update Video' : 'Add Video'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVideosPage;
